import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, downloadFile, openPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatMoney, formatRate, formatWritten, formatUK } from "@/lib/format";
import {
  ArrowLeft, FileText, Download, FileStack, FileArchive, Printer,
  Loader2, CheckCircle2, Copy, Ban, RefreshCcw, ShieldCheck, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";

const statusBadge = {
  draft: "bg-slate-100 text-slate-700", finalised: "bg-emerald-100 text-emerald-700",
  superseded: "bg-amber-100 text-amber-700", cancelled: "bg-red-100 text-red-700",
};

export default function DividendDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [div, setDiv] = useState(null);
  const [company, setCompany] = useState(null);
  const [selectedLine, setSelectedLine] = useState(0);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [finaliseOpen, setFinaliseOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/dividends/${id}`).then((r) => {
      setDiv(r.data);
      const cid = r.data.company_id;
      const snap = r.data.company_snapshot;
      if (snap) setCompany(snap);
      else api.get(`/companies/${cid}`).then((c) => setCompany(c.data));
    });
  };
  useEffect(() => { load(); }, [id]);

  if (!div || !company) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cass-blue" /></div>;

  const lines = div.lines || [];
  const finalised = div.status === "finalised";

  const finalise = async () => {
    setBusy(true);
    try {
      await api.post(`/dividends/${id}/finalise`, { confirmation: true });
      toast.success("Dividend finalised");
      setFinaliseOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doAction = async (path, msg) => {
    setBusy(true);
    try {
      const res = await api.post(`/dividends/${id}/${path}`);
      toast.success(msg);
      if (path === "duplicate" || path === "supersede") navigate(`/dividends/${res.data.id}`);
      else load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const dl = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error("Download failed"); } finally { setBusy(false); } };

  const line = lines[selectedLine];

  return (
    <div data-testid="dividend-detail-page">
      <button onClick={() => navigate("/history")} className="mb-4 flex items-center gap-1 text-sm text-cass-navy/60 hover:text-cass-blue">
        <ArrowLeft className="h-4 w-4" /> Back to history
      </button>

      <PageHeader title={div.period_label || "Dividend"} description={`${div.description} · ${div.share_class}`}>
        <Badge className={`${statusBadge[div.status]} border-0 capitalize text-sm px-3 py-1`} data-testid="detail-status">{div.status}</Badge>
      </PageHeader>

      {div.supersedes && <Alert className="mb-4 border-amber-200 bg-amber-50"><AlertDescription className="text-amber-700">This is a corrected version of a superseded record.</AlertDescription></Alert>}
      {div.status === "superseded" && <Alert className="mb-4 border-amber-200 bg-amber-50"><AlertDescription className="text-amber-700">This record has been superseded and should no longer be used.</AlertDescription></Alert>}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left: summary + calc */}
        <div className="space-y-6 lg:col-span-2">
          <Card className="border-cass-border p-6 shadow-sm">
            <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
              <Info label="Period end" value={formatWritten(div.period_end)} />
              <Info label="Record date" value={formatWritten(div.record_date)} />
              <Info label="Payment date" value={formatWritten(div.payment_date)} />
              <Info label="Voucher issue" value={formatWritten(div.voucher_issue_date)} />
              <Info label="Signatory" value={`${div.signatory_name || "—"}${div.signatory_position ? ", " + div.signatory_position : ""}`} />
              <Info label="Created by" value={div.created_by} />
            </div>
          </Card>

          {lines.map((l, idx) => (
            <Card key={idx} className="border-cass-border p-6 shadow-sm" data-testid={`calc-line-${idx}`}>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-head text-base font-semibold text-cass-navy">{l.recipient_name}</h3>
                <span className="text-xs text-cass-navy/50">{l.share_class}</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-cass-border text-xs uppercase tracking-wider text-cass-navy/50">
                    <th className="pb-2 text-left font-medium">Month</th>
                    <th className="pb-2 text-right font-medium">Number of {l.share_class} shares</th>
                    <th className="pb-2 text-right font-medium">Rate/share</th>
                    <th className="pb-2 text-right font-medium">Payable</th>
                  </tr>
                </thead>
                <tbody>
                  {l.monthly.map((m) => (
                    <tr key={m.month_key} className="border-b border-cass-border/60">
                      <td className="py-2 text-cass-navy/70">{m.month_label}</td>
                      <td className="py-2 text-right tabular">{m.eligible_shares}</td>
                      <td className="py-2 text-right tabular">{formatRate(m.rate)}</td>
                      <td className="py-2 text-right tabular font-medium">{formatMoney(m.amount)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="pt-3 font-semibold text-cass-navy" colSpan={3}>{div.is_quarterly ? "Total Dividend this quarter" : "Total Dividend for this period"}</td>
                    <td className="pt-3 text-right tabular text-base font-bold text-cass-navy" data-testid={`line-total-${idx}`}>{formatMoney(l.total)}</td>
                  </tr>
                </tbody>
              </table>
              {finalised && (
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => dl(() => openPdf(`/dividends/${id}/voucher/${idx}.pdf`))} data-testid={`view-voucher-${idx}`}><FileText className="mr-1 h-4 w-4" /> View</Button>
                  <Button variant="outline" size="sm" onClick={() => dl(() => downloadFile(`/dividends/${id}/voucher/${idx}.pdf`, "voucher.pdf"))} data-testid={`download-voucher-${idx}`}><Download className="mr-1 h-4 w-4" /> PDF</Button>
                </div>
              )}
            </Card>
          ))}

          <Card className="border-cass-border bg-cass-surface p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="font-head text-lg font-semibold text-cass-navy">Total dividend declared</span>
              <span className="tabular text-2xl font-bold text-cass-navy" data-testid="detail-grand-total">{formatMoney(div.grand_total)}</span>
            </div>
          </Card>

          <Card className="border-cass-border p-6 shadow-sm" data-testid="payment-summary-card">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="font-head text-lg font-semibold text-cass-navy">Payment summary</h3>
                <p className="text-sm text-cass-navy/50">What to pay each shareholder{div.payment_date ? ` on ${formatWritten(div.payment_date)}` : ""}.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => dl(() => openPdf(`/dividends/${id}/payment-summary.pdf`))} data-testid="view-payment-summary"><FileText className="mr-1 h-4 w-4" /> View</Button>
                <Button variant="outline" size="sm" onClick={() => dl(() => downloadFile(`/dividends/${id}/payment-summary.pdf`, "payment-summary.pdf"))} data-testid="download-payment-summary"><Download className="mr-1 h-4 w-4" /> PDF</Button>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-cass-border text-xs uppercase tracking-wider text-cass-navy/50">
                  <th className="pb-2 text-left font-medium">Shareholder</th>
                  <th className="pb-2 text-left font-medium">Reference</th>
                  <th className="pb-2 text-right font-medium">Amount to pay</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-b border-cass-border/60">
                    <td className="py-2 text-cass-navy">{l.recipient_name}</td>
                    <td className="py-2 text-cass-navy/60">{l.reference || "—"}</td>
                    <td className="py-2 text-right tabular font-medium">{formatMoney(l.total)}</td>
                  </tr>
                ))}
                <tr>
                  <td className="pt-3 font-semibold text-cass-navy" colSpan={2}>Total to pay</td>
                  <td className="pt-3 text-right tabular text-base font-bold text-cass-navy" data-testid="payment-summary-total">{formatMoney(div.grand_total)}</td>
                </tr>
              </tbody>
            </table>
          </Card>
        </div>

        {/* Right: actions + preview */}
        <div className="space-y-6">
          <Card className="border-cass-border p-6 shadow-sm">
            <h3 className="mb-4 font-head text-lg font-semibold text-cass-navy">Actions</h3>
            <div className="space-y-2">
              {!finalised && div.status === "draft" && isAdmin && (
                <Button onClick={() => setFinaliseOpen(true)} data-testid="finalise-button" className="w-full bg-cass-blue text-white hover:bg-cass-bluehover">
                  <CheckCircle2 className="mr-2 h-4 w-4" /> Finalise Dividend
                </Button>
              )}
              {finalised && (
                <>
                  <Button onClick={() => dl(() => downloadFile(`/dividends/${id}/vouchers/combined.pdf`, "vouchers.pdf"))} disabled={busy} data-testid="download-combined" className="w-full bg-cass-blue text-white hover:bg-cass-bluehover">
                    <FileStack className="mr-2 h-4 w-4" /> Combined PDF
                  </Button>
                  <Button variant="outline" onClick={() => dl(() => downloadFile(`/dividends/${id}/vouchers/zip`, "vouchers.zip"))} disabled={busy} data-testid="download-zip" className="w-full">
                    <FileArchive className="mr-2 h-4 w-4" /> Download ZIP
                  </Button>
                  <Button variant="outline" onClick={() => dl(() => openPdf(`/dividends/${id}/vouchers/combined.pdf`))} disabled={busy} data-testid="print-button" className="w-full">
                    <Printer className="mr-2 h-4 w-4" /> Print / Preview
                  </Button>
                </>
              )}
              <Button variant="outline" onClick={() => doAction("duplicate", "Duplicated to next period")} disabled={busy} data-testid="duplicate-button" className="w-full">
                <Copy className="mr-2 h-4 w-4" /> Duplicate for next period
              </Button>
              <Button variant="outline" onClick={() => dl(() => openPdf(`/dividends/${id}/payment-summary.pdf`))} disabled={busy} data-testid="actions-payment-summary" className="w-full">
                <ClipboardList className="mr-2 h-4 w-4" /> Payment Summary
              </Button>
              {div.status === "draft" && (
                <Button variant="outline" onClick={() => doAction("cancel", "Draft cancelled")} disabled={busy} data-testid="cancel-button" className="w-full text-destructive">
                  <Ban className="mr-2 h-4 w-4" /> Cancel draft
                </Button>
              )}
              {finalised && isAdmin && (
                <Button variant="outline" onClick={() => doAction("supersede", "Superseded — corrected draft created")} disabled={busy} data-testid="supersede-button" className="w-full text-amber-600">
                  <RefreshCcw className="mr-2 h-4 w-4" /> Supersede & correct
                </Button>
              )}
            </div>
            {div.confirmation && (
              <div className="mt-4 rounded-md bg-emerald-50 p-3 text-xs text-emerald-800">
                <ShieldCheck className="mb-1 h-4 w-4" />
                Confirmed by {div.confirmation.user} on {formatUK(div.confirmation.datetime)}.
              </div>
            )}
          </Card>

          {/* On-screen voucher preview */}
          {lines.length > 0 && (
            <Card className="border-cass-border p-0 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between border-b border-cass-border bg-cass-surface p-3">
                <span className="text-xs font-bold uppercase tracking-wider text-cass-navy/50">Voucher preview</span>
                {lines.length > 1 && (
                  <Select value={String(selectedLine)} onValueChange={(v) => setSelectedLine(Number(v))}>
                    <SelectTrigger className="h-8 w-40 text-xs" data-testid="preview-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{lines.map((l, i) => <SelectItem key={i} value={String(i)}>{l.recipient_name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
              <VoucherPreview company={company} div={div} line={line} />
            </Card>
          )}
        </div>
      </div>

      <AlertDialog open={finaliseOpen} onOpenChange={setFinaliseOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-head text-cass-navy">Finalise dividend</AlertDialogTitle>
            <AlertDialogDescription>Once finalised, figures are stored as an immutable snapshot and cannot be edited directly.</AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-start gap-3 rounded-md border border-cass-border p-3">
            <Checkbox checked={confirmChecked} onCheckedChange={setConfirmChecked} data-testid="confirm-checkbox" className="mt-0.5" />
            <span className="text-sm text-cass-navy/80">
              I confirm that the company has approved this dividend and that the necessary checks regarding distributable reserves and the legality of the dividend have been completed.
            </span>
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={!confirmChecked || busy} onClick={finalise} data-testid="confirm-finalise" className="bg-cass-blue text-white hover:bg-cass-bluehover">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Finalise
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Info({ label, value }) {
  return <div><div className="text-xs uppercase tracking-wider text-cass-navy/40">{label}</div><div className="mt-0.5 font-medium text-cass-navy">{value || "—"}</div></div>;
}

function VoucherPreview({ company, div, line }) {
  if (!line) return null;
  const heading = div.is_quarterly ? "Quarterly Dividend Tax Voucher" : "Dividend Tax Voucher";
  const totalLabel = div.is_quarterly ? "Total Dividend this quarter" : "Total Dividend for this period";
  return (
    <div className="bg-white p-6 text-[11px] text-cass-navy" data-testid="voucher-preview">
      <div className="text-center">
        {company.logo ? <img src={company.logo} alt="logo" className="mx-auto max-h-14 object-contain" /> : <div className="font-head text-base font-bold">{company.name}</div>}
        <h2 className="mt-2 font-head text-sm font-bold">{heading}</h2>
      </div>
      <div className="my-3 h-0.5" style={{ background: company.brand_accent_color || "#2EA3FD" }} />
      <p>Dividend in respect of the period ended {formatWritten(div.period_end)}, payable to shareholders registered at the close of business on {formatWritten(div.record_date)}.</p>
      <div className="my-3">
        <div className="font-bold">Shareholder Recipient: {line.recipient_name}</div>
        {line.share_class && <div className="text-cass-navy/60">Share class: {line.share_class}</div>}
        {line.address && <div className="text-cass-navy/60 whitespace-pre-line">{line.address}</div>}
      </div>
      <table className="w-full border-collapse">
        <thead>
          <tr style={{ background: company.brand_primary_color || "#0B1A2F" }} className="text-white">
            <th className="p-1.5 text-left">Month</th>
            <th className="p-1.5 text-right">No. of {line.share_class} Shares</th>
            <th className="p-1.5 text-right">Rate/share</th>
            <th className="p-1.5 text-right">Payable</th>
          </tr>
        </thead>
        <tbody>
          {line.monthly.map((m) => (
            <tr key={m.month_key} className="border-b border-cass-border">
              <td className="p-1.5">{m.month_label}</td>
              <td className="p-1.5 text-right tabular">{m.eligible_shares}</td>
              <td className="p-1.5 text-right tabular">{formatRate(m.rate)}</td>
              <td className="p-1.5 text-right tabular">{formatMoney(m.amount)}</td>
            </tr>
          ))}
          <tr className="bg-cass-surface font-bold">
            <td className="p-1.5" colSpan={3}>{totalLabel}</td>
            <td className="p-1.5 text-right tabular">{formatMoney(line.total)}</td>
          </tr>
        </tbody>
      </table>
      <div className="my-3 space-y-1 text-cass-navy/70">
        <p>{company.voucher_safe_keeping_text || "This voucher should be kept in a safe place."}</p>
        <p>{company.voucher_tax_responsibility_text || "It is your responsibility to declare any tax payable on dividends paid to you."}</p>
      </div>
      <div className="mt-6">
        {company.signature_image && <img src={company.signature_image} alt="signature" className="max-h-10" />}
        <div className="mt-1 border-t border-cass-navy pt-1 inline-block min-w-[160px] font-bold">{div.signatory_name}</div>
        <div className="text-cass-navy/60">{div.signatory_position}</div>
        <div className="text-cass-navy/60">Voucher issue date: {formatWritten(div.voucher_issue_date)}</div>
      </div>
      <div className="mt-4 border-t border-cass-border pt-2 text-center text-[9px] text-cass-navy/50">
        {(company.trading_address || company.registered_office || "").replace(/\n/g, ", ")}
        {company.phone && ` | Tel: ${company.phone}`}{company.email && ` | ${company.email}`}
        <div>{company.name}{company.company_number && ` | Company No. ${company.company_number}`}</div>
      </div>
    </div>
  );
}
